/**
 * Iter59.p19 / p20 — Shared Bento dashboard tiles.
 *
 * Used by both:
 *   • `/pages/ModuleDashboard.jsx`  → module workspace at /m/{code}
 *   • `/pages/Home.jsx`             → landing dashboard at /
 *
 * All tiles are self-contained visual components — parent supplies data and
 * navigation callbacks. Grid placement is provided via inline `style` props
 * so callers can override (e.g. landing uses a different Welcome-tile
 * heading but identical grid slot).
 */
import React, { useMemo, useState } from "react";
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import {
    Activity, Banknote, Clock, ArrowRight, AlertTriangle, AlertOctagon, Info,
    TrendingUp,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import EmptyState from "./EmptyState";
import ModuleIdChip from "./ModuleIdChip";
import useChartColors from "../hooks/useChartColors";

/* Formatting helpers */
export const fmtUSD0 = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
export const fmtUSD2 = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const todayIso = () => new Date().toISOString().slice(0, 10);
export const fmtDateShort = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    } catch { return String(iso).slice(0, 10); }
};
export const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return String(iso).slice(0, 16); }
};
export const fmtTodayLong = () => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
};

/* Payroll stage order for the progress ring */
export const PAYROLL_STAGES = ["draft", "prepared", "dept_confirmed", "finance_reviewed", "approved", "funded", "paid", "closed"];
export const stageIndex = (status) => {
    const i = PAYROLL_STAGES.indexOf(status);
    return i < 0 ? 0 : i;
};
export const stageLabel = (status) => (status || "draft").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

/* ------------------------------------------------------------------ */
/* Welcome tile                                                       */
/* ------------------------------------------------------------------ */
export function WelcomeTile({
    caption = "WELCOME BACK",
    heading,
    subheading,
    kpis,
    loading,
    onKpiClick,
    style,
    testid = "md-tile-welcome",
}) {
    return (
        <div className="asl-bento-tile asl-bento-welcome" data-testid={testid}
            style={{ gridColumn: "1 / span 8", gridRow: "1", ...style }}>
            <div className="asl-bento-welcome__deco" aria-hidden="true" />
            <div className="asl-bento-welcome__body">
                <div>
                    <div className="text-label" style={{ color: "var(--asl-crimson-2)" }}>{caption}</div>
                    <h1 className="text-page-title mt-1" data-testid="md-welcome-title">{heading}</h1>
                    <div className="text-sm mt-1" style={{ color: "var(--asl-text-2)" }}>
                        {subheading}
                    </div>
                </div>
                <div className="asl-bento-welcome__kpis">
                    <MiniKPI label="Active Workers" value={kpis?.active_employees?.value ?? "—"}
                        loading={loading} onClick={onKpiClick?.active} testid="md-welcome-kpi-active" />
                    <MiniKPI label="Present Today" value={kpis?.present_today?.value ?? "—"}
                        loading={loading} onClick={onKpiClick?.present} testid="md-welcome-kpi-present" />
                    <MiniKPI label="Weekly Cost" value={fmtUSD0(kpis?.week_cost?.value ?? 0)}
                        loading={loading} onClick={onKpiClick?.weekly} accent testid="md-welcome-kpi-weekly" />
                    <MiniKPI label="Open Exceptions" value={kpis?.exceptions_open?.value ?? 0}
                        loading={loading} onClick={onKpiClick?.exceptions}
                        tone={(kpis?.exceptions_open?.value ?? 0) > 0 ? "danger" : "default"}
                        testid="md-welcome-kpi-exceptions" />
                </div>
            </div>
        </div>
    );
}

export function MiniKPI({ label, value, loading, onClick, accent, tone, testid }) {
    const color = tone === "danger"
        ? "var(--status-error)"
        : (accent ? "var(--asl-crimson)" : "var(--kpi-number-color)");
    return (
        <button type="button" onClick={onClick} className="asl-bento-minikpi" data-testid={testid}>
            <div className="text-label">{label}</div>
            <div className="text-metric-sm mono mt-1" style={{ color }}>
                {loading ? <span className="asl-shimmer inline-block w-12 h-4 rounded" /> : value}
            </div>
        </button>
    );
}

/* ------------------------------------------------------------------ */
/* Weekly bars tile                                                   */
/* ------------------------------------------------------------------ */
export function WeeklyBarsTile({ last7, loading, onClick, style }) {
    const c = useChartColors();
    const maxPresent = Math.max(1, ...last7.map((d) => (d.present || 0) + (d.late || 0) + (d.absent || 0)));
    const todayStr = todayIso();
    return (
        <div className="asl-bento-tile" data-testid="md-tile-weekly-bars"
            style={{ gridColumn: "9 / span 4", gridRow: "1 / span 2", cursor: onClick ? "pointer" : "default", ...style }}
            onClick={onClick}>
            <div className="asl-section-title mb-4">This week</div>
            {loading ? (
                <div className="asl-shimmer w-full rounded-lg" style={{ height: 220 }} />
            ) : last7.length === 0 ? (
                <EmptyState icon={Activity} title="No attendance yet" description="Data will populate as attendance is captured." />
            ) : (
                <div className="asl-weekly-bars">
                    {last7.map((d) => {
                        const total = (d.present || 0) + (d.late || 0) + (d.absent || 0);
                        const heightPct = Math.round((total / maxPresent) * 100);
                        const presentPct = total ? Math.round((d.present || 0) / total * 100) : 0;
                        const latePct = total ? Math.round((d.late || 0) / total * 100) : 0;
                        const isToday = d.date === todayStr;
                        return (
                            <div key={d.date} className="asl-weekly-bars__col"
                                data-testid={`md-weekly-bar-${d.date}`}>
                                <div className="asl-weekly-bars__stack" style={{ height: `${Math.max(heightPct, 6)}%` }}>
                                    <div style={{ height: `${presentPct}%`, background: c.statusSuccess }} />
                                    <div style={{ height: `${latePct}%`, background: c.statusWarning }} />
                                    <div style={{ flex: 1, background: "var(--surface-muted)" }} />
                                </div>
                                <div className={`asl-weekly-bars__label ${isToday ? "asl-weekly-bars__label--today" : ""}`}>
                                    {new Date(d.date).toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 3)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="mt-4 flex items-center gap-4 text-xs" style={{ color: "var(--asl-text-2)" }}>
                <LegendDot color={c.statusSuccess} label="Present" />
                <LegendDot color={c.statusWarning} label="Late" />
                <LegendDot color="var(--surface-muted)" label="Absent" />
            </div>
        </div>
    );
}

function LegendDot({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 8, height: 8, borderRadius: 999, background: color, display: "inline-block" }} />
            {label}
        </span>
    );
}

/* ------------------------------------------------------------------ */
/* Payroll Ring tile                                                  */
/* ------------------------------------------------------------------ */
export function PayrollRingTile({ period, moduleChip, loading, onClick, style }) {
    const status = period?.status || "draft";
    const idx = stageIndex(status);
    const pct = (idx / (PAYROLL_STAGES.length - 1)) * 100;
    const netTotal = period?.totals?.net_payable_total ?? period?.totals?.net_payable ?? 0;
    const label = stageLabel(status);
    const dates = period ? `${fmtDateShort(period.start_date)} – ${fmtDateShort(period.end_date)}` : "—";
    return (
        <div className="asl-bento-tile" data-testid="md-tile-payroll-ring"
            style={{ gridColumn: "1 / span 4", gridRow: "2", cursor: period ? "pointer" : "default", ...style }}
            onClick={onClick}>
            <div className="flex items-center justify-between mb-2">
                <div className="asl-section-title">Current Payroll Period</div>
                {moduleChip && (
                    <span className="asl-status-pill" data-testid="md-payroll-module-chip"
                        style={{ fontSize: 10, background: "rgba(168, 50, 74, 0.10)", color: "var(--asl-crimson)" }}>
                        {moduleChip}
                    </span>
                )}
            </div>
            {loading ? (
                <div className="asl-shimmer w-full rounded-lg" style={{ height: 180 }} />
            ) : !period ? (
                <EmptyState icon={Banknote} title="No period yet" description="Create a payroll period to begin." />
            ) : (
                <div className="flex items-center gap-5">
                    <ProgressRing size={128} strokeWidth={12} percent={pct}>
                        <div className="text-center">
                            <div className="text-metric mono asl-navy-text" style={{ fontSize: 18, lineHeight: 1.15 }} data-testid="md-payroll-ring-amount">
                                {fmtUSD0(netTotal)}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: "var(--asl-text-3)" }}>
                                Net Payable
                            </div>
                        </div>
                    </ProgressRing>
                    <div className="min-w-0">
                        <div className="asl-status-pill" data-testid="md-payroll-ring-status">
                            {label}
                        </div>
                        <div className="text-sm mt-2 truncate" style={{ color: "var(--asl-text-2)" }}>{dates}</div>
                        <div className="text-xs mt-1 truncate" style={{ color: "var(--asl-text-3)" }}>{period.name}</div>
                        <div className="text-xs mt-3 asl-crimson-text font-semibold flex items-center gap-1">
                            View <ArrowRight className="h-3 w-3" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ProgressRing({ size, strokeWidth, percent, children }) {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.max(0, Math.min(100, percent)) / 100) * circumference;
    return (
        <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
            <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden="true">
                <circle cx={size / 2} cy={size / 2} r={radius}
                    stroke="var(--asl-divider)" strokeWidth={strokeWidth} fill="none" />
                <circle cx={size / 2} cy={size / 2} r={radius}
                    stroke="var(--asl-crimson)" strokeWidth={strokeWidth} fill="none"
                    strokeDasharray={circumference} strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.6s ease" }} />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {children}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Projected Cost tile — Iter60.p30 (Task C)                           */
/* ------------------------------------------------------------------ */
export function ProjectedCostTile({ data, loading, onClick, onShowBreakdown,
                                    style, moduleId, hidden }) {
    const c = useChartColors();
    if (hidden) return null;
    const series = data?.series || [];
    const total = data?.total_projected || 0;
    const actual = data?.actual_so_far || 0;
    const projected = data?.projected_remaining || 0;
    const label = data?.period_label || "—";
    // Iter60.p39 — Master-scope aggregation deliberately returns
    // `period_id = None` because the visible modules run on different
    // payroll windows (see `_aggregate_projected_cost`). Gating the
    // chart + drill-down on `period_id` therefore hides the tile for
    // admin/HR even though the aggregate has real data. Use a
    // data-presence gate instead so master scope surfaces the
    // per-module drill-down that Item B requires.
    const perModuleCount = Array.isArray(data?.per_module) ? data.per_module.length : 0;
    const hasData = !!data && (
        series.length > 0
        || total > 0
        || actual > 0
        || projected > 0
        || perModuleCount > 0
    );
    const testidBase = moduleId ? moduleId : "generic";
    const totalTid = `dashboard-projected-cost-total-${testidBase}`;
    const chartTid = `dashboard-projected-cost-chart-${testidBase}`;
    // Iter60.p31 — tile click now opens the read-only breakdown modal
    // (was: navigate to /payroll/{period_id}, which exposed
    // Prepare/Recompute affordances). The tile is purely informational.
    const openModal = () => { if (hasData) onShowBreakdown?.(); };
    return (
        <div className="asl-bento-tile" data-testid="md-tile-projected-cost"
            onClick={openModal}
            style={{ gridColumn: "5 / span 4", gridRow: "2",
                     cursor: hasData ? "pointer" : "default", ...style }}>
            <div className="flex items-center justify-between mb-1">
                <div>
                    <div className="asl-section-title flex items-center gap-1.5">
                        <TrendingUp className="h-3.5 w-3.5" />
                        Projected Cost
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--asl-text-2)" }}
                         data-testid="md-projected-cost-period-label">
                        {label}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openModal(); }}
                    className="text-metric-sm mono asl-navy-text hover:underline"
                    data-testid={totalTid}
                    disabled={!hasData}
                    style={{ cursor: hasData ? "pointer" : "default", background: "transparent", border: 0, padding: 0 }}
                >
                    {fmtUSD0(total)}
                </button>
            </div>
            <div className="text-xs mb-2" style={{ color: "var(--asl-text-2)" }}>
                <span data-testid="md-projected-cost-actual">
                    Actual so far: <span className="mono" style={{ color: "var(--asl-text-1)" }}>{fmtUSD0(actual)}</span>
                </span>
                <span className="mx-2">·</span>
                <span data-testid="md-projected-cost-remaining">
                    Projected: <span className="mono" style={{ color: "var(--asl-crimson-2)" }}>{fmtUSD0(projected)}</span>
                </span>
            </div>
            {loading ? (
                <div className="asl-shimmer w-full rounded-lg" style={{ height: 110 }} />
            ) : !hasData ? (
                <EmptyState icon={Banknote} title="No open period"
                            description="Projection appears once a draft/prepared period exists." />
            ) : (
                <div data-testid={chartTid}>
                    <ResponsiveContainer width="100%" height={110}>
                        <BarChart data={series} margin={{ top: 2, right: 6, left: -22, bottom: 0 }}>
                            <CartesianGrid vertical={false} stroke={c.grid} strokeDasharray="3 3" />
                            <XAxis dataKey="date" tickFormatter={fmtDateShort} interval={Math.max(0, Math.floor(series.length / 6))}
                                   tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: c.axis }} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: c.axis }} width={44}
                                   tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                            <Tooltip
                                formatter={(v, _, item) =>
                                    [fmtUSD2(v), item?.payload?.is_projected ? "Projected" : "Actual"]}
                                labelFormatter={fmtDateShort}
                                contentStyle={{ background: c.tooltipBg, color: c.tooltipText, border: "1px solid var(--border-default)" }}
                                itemStyle={{ color: c.tooltipText }}
                                labelStyle={{ color: c.tooltipText }}
                            />
                            <Bar dataKey="cost" radius={[3, 3, 0, 0]}>
                                {series.map((s, i) => (
                                    <Cell key={i}
                                          fill={s.is_projected ? c.series[0] : "var(--asl-crimson)"}
                                          fillOpacity={s.is_projected ? 0.45 : 1}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

/* Iter60.p31 — Weekly Cost read-only breakdown modal.
   Purely informational: no Prepare/Recompute affordances, no navigation. */
export function WeeklyCostModal({ open, onClose, data, loading }) {
    const [tab, setTab] = useState("days");
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl" data-testid="md-weekly-cost-modal">
                <DialogHeader>
                    <DialogTitle>
                        Weekly Cost · {fmtUSD0(data?.total_cost || 0)} · last 7 days
                    </DialogTitle>
                </DialogHeader>
                <p className="text-xs mb-3" style={{ color: "var(--asl-text-3)" }}>
                    Sum of <span className="mono">daily_rate × multiplier_applied</span>
                    across all present-like rows (present, late, partial, weekend
                    present, holiday, paid leave) in the last 7 days. Read-only.
                </p>
                <div className="flex gap-2 mb-2 text-xs">
                    <button
                        type="button"
                        className={`px-3 py-1 rounded ${tab === "days" ? "asl-navy-bg text-white" : "bg-transparent asl-navy-text"}`}
                        onClick={() => setTab("days")}
                        data-testid="md-weekly-cost-tab-days"
                    >Day-by-day</button>
                    <button
                        type="button"
                        className={`px-3 py-1 rounded ${tab === "employees" ? "asl-navy-bg text-white" : "bg-transparent asl-navy-text"}`}
                        onClick={() => setTab("employees")}
                        data-testid="md-weekly-cost-tab-employees"
                    >Top contributors</button>
                </div>
                {loading ? (
                    <div className="asl-shimmer w-full rounded" style={{ height: 240 }} />
                ) : tab === "days" ? (
                    <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                        <table className="w-full text-xs" data-testid="md-weekly-cost-days-table">
                            <thead className="asl-navy-bg text-white sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Date</th>
                                    <th className="text-right px-3 py-2">Attended</th>
                                    <th className="text-right px-3 py-2">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.days || []).map((d) => (
                                    <tr key={d.date}
                                        className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                        data-testid={`md-weekly-cost-day-${d.date}`}>
                                        <td className="px-3 py-1.5 mono">{d.date}</td>
                                        <td className="px-3 py-1.5 mono text-right">{d.attended}</td>
                                        <td className="px-3 py-1.5 mono text-right">{fmtUSD2(d.cost)}</td>
                                    </tr>
                                ))}
                                {(data?.days || []).length === 0 && (
                                    <tr><td colSpan={3} className="px-3 py-4 text-center"
                                            style={{ color: "var(--asl-text-3)" }}>
                                        No attendance recorded in the window.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                        <table className="w-full text-xs" data-testid="md-weekly-cost-employees-table">
                            <thead className="asl-navy-bg text-white sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Employee</th>
                                    <th className="text-left px-3 py-2">Module ID</th>
                                    <th className="text-right px-3 py-2">7-day cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data?.contributors || []).length === 0 ? (
                                    <tr><td colSpan={3} className="px-3 py-4 text-center"
                                            style={{ color: "var(--asl-text-3)" }}>
                                        No contributors yet.
                                    </td></tr>
                                ) : (data?.contributors || []).map((r) => (
                                    <tr key={r.employee_id}
                                        className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                        data-testid={`md-weekly-cost-emp-${r.employee_id}`}>
                                        <td className="px-3 py-1.5">{r.employee_name || "—"}</td>
                                        <td className="px-3 py-1.5">
                                            <ModuleIdChip
                                                value={r.module_employee_id}
                                                testid={`md-weekly-cost-mid-${r.employee_id}`}
                                            />
                                        </td>
                                        <td className="px-3 py-1.5 mono text-right">{fmtUSD2(r.cost || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}
                            data-testid="md-weekly-cost-modal-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* Projected Cost — breakdown modal (day-by-day + top employees) */
export function ProjectedCostModal({ open, onClose, data, contributors }) {
    const [tab, setTab] = useState("days");
    if (!data) return null;
    const series = data.series || [];
    const total = data.total_projected || 0;
    const actual = data.actual_so_far || 0;
    const projected = data.projected_remaining || 0;
    // Iter60.p38 — Optional per-module drill-down. Present when the
    // caller aggregates across modules (Home dashboard) and populates
    // `data.per_module`; hidden otherwise (per-module dashboard modal).
    const perModule = Array.isArray(data.per_module) ? data.per_module : [];
    const showByModule = perModule.length > 1;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-3xl" data-testid="md-projected-cost-modal">
                <DialogHeader>
                    <DialogTitle>Projected Cost — {data.period_label || "period"}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                    <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                        <div className="text-[color:var(--asl-muted)]">Actual so far</div>
                        <div className="text-lg font-bold asl-navy-text mono">{fmtUSD0(actual)}</div>
                    </div>
                    <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                        <div className="text-[color:var(--asl-muted)]">Projected remaining</div>
                        <div className="text-lg font-bold asl-crimson-text mono">{fmtUSD0(projected)}</div>
                    </div>
                    <div className="rounded-md border border-[color:var(--asl-border,#E5E7EB)] p-2">
                        <div className="text-[color:var(--asl-muted)]">Total projected</div>
                        <div className="text-lg font-bold asl-navy-text mono">{fmtUSD0(total)}</div>
                    </div>
                </div>
                <div className="flex gap-2 mb-2 text-xs">
                    <button
                        type="button"
                        className={`px-3 py-1 rounded ${tab === "days" ? "asl-navy-bg text-white" : "bg-transparent asl-navy-text"}`}
                        onClick={() => setTab("days")}
                        data-testid="md-projected-cost-tab-days"
                    >Day-by-day</button>
                    <button
                        type="button"
                        className={`px-3 py-1 rounded ${tab === "employees" ? "asl-navy-bg text-white" : "bg-transparent asl-navy-text"}`}
                        onClick={() => setTab("employees")}
                        data-testid="md-projected-cost-tab-employees"
                    >Top contributors</button>
                    {showByModule && (
                        <button
                            type="button"
                            className={`px-3 py-1 rounded ${tab === "modules" ? "asl-navy-bg text-white" : "bg-transparent asl-navy-text"}`}
                            onClick={() => setTab("modules")}
                            data-testid="md-projected-cost-tab-modules"
                        >By module</button>
                    )}
                </div>
                {tab === "days" ? (
                    <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                        <table className="w-full text-xs" data-testid="md-projected-cost-days-table">
                            <thead className="asl-navy-bg text-white sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Date</th>
                                    <th className="text-left px-3 py-2">Type</th>
                                    <th className="text-right px-3 py-2">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {series.map((s) => (
                                    <tr key={s.date} className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                        data-testid={`md-projected-cost-day-${s.date}`}>
                                        <td className="px-3 py-1.5 mono">{s.date}</td>
                                        <td className={`px-3 py-1.5 ${s.is_projected ? "asl-crimson-text" : "asl-navy-text"}`}>
                                            {s.is_projected ? "Projected" : "Actual"}
                                        </td>
                                        <td className="px-3 py-1.5 mono text-right">{fmtUSD2(s.cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : tab === "modules" ? (
                    // Iter60.p38 — Per-module breakdown for the Home
                    // aggregate. Each row is one module's current open
                    // payroll period; columns mirror the aggregate KPI.
                    <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                        <table className="w-full text-xs" data-testid="md-projected-cost-modules-table">
                            <thead className="asl-navy-bg text-white sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Module</th>
                                    <th className="text-right px-3 py-2">Actual so far</th>
                                    <th className="text-right px-3 py-2">Projected remaining</th>
                                    <th className="text-right px-3 py-2">Total projected</th>
                                </tr>
                            </thead>
                            <tbody>
                                {perModule.map((m) => (
                                    <tr key={m.module_id}
                                        className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                        data-testid={`md-projected-cost-module-${m.code || m.module_id}`}>
                                        <td className="px-3 py-1.5 asl-navy-text font-medium">
                                            {m.name || m.code || m.module_id}
                                        </td>
                                        <td className="px-3 py-1.5 mono text-right">{fmtUSD2(m.actual_so_far || 0)}</td>
                                        <td className="px-3 py-1.5 mono text-right asl-crimson-text">{fmtUSD2(m.projected_remaining || 0)}</td>
                                        <td className="px-3 py-1.5 mono text-right font-semibold asl-navy-text">{fmtUSD2(m.total_projected || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="max-h-80 overflow-auto border border-[color:var(--asl-border,#E5E7EB)] rounded-md">
                        <table className="w-full text-xs" data-testid="md-projected-cost-employees-table">
                            <thead className="asl-navy-bg text-white sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2">Employee</th>
                                    <th className="text-left px-3 py-2">Module ID</th>
                                    <th className="text-right px-3 py-2">Contribution</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(contributors || []).length === 0 ? (
                                    <tr><td colSpan={3} className="px-3 py-4 text-center"
                                            style={{ color: "var(--asl-text-3)" }}>
                                        No contributor data yet.
                                    </td></tr>
                                ) : (contributors || []).map((r) => (
                                    <tr key={r.employee_id}
                                        className="border-t border-[color:var(--asl-border,#E5E7EB)]"
                                        data-testid={`md-projected-cost-emp-${r.employee_id}`}>
                                        <td className="px-3 py-1.5">{r.name || "—"}</td>
                                        <td className="px-3 py-1.5">
                                            <ModuleIdChip
                                                value={r.module_employee_id}
                                                testid={`md-projected-cost-mid-${r.employee_id}`}
                                            />
                                        </td>
                                        <td className="px-3 py-1.5 mono text-right">{fmtUSD2(r.amount || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}
                            data-testid="md-projected-cost-modal-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Cost trend tile (legacy — kept for master page consumers)          */
/* ------------------------------------------------------------------ */
export function CostTrendTile({ data, total, loading, onClick, style, gradientId = "mgrad-cost", moduleId }) {
    const c = useChartColors();
    // Iter60.p28 — module-scoped data-testids per spec ("dashboard-cost-
    // trend-total-{module_id}" + "-chart-{module_id}"). Fall back to the
    // legacy ids when moduleId isn't passed (master view).
    const totalTid = moduleId ? `dashboard-cost-trend-total-${moduleId}` : "md-cost-trend-total";
    const chartTid = moduleId ? `dashboard-cost-trend-chart-${moduleId}` : "md-cost-trend-chart";
    return (
        <div className="asl-bento-tile" data-testid="md-tile-cost-trend"
            style={{ gridColumn: "5 / span 4", gridRow: "2", cursor: onClick ? "pointer" : "default", ...style }}
            onClick={onClick}>
            <div className="flex items-center justify-between mb-2">
                <div className="asl-section-title">Cost trend · 30 days</div>
                <div className="text-metric-sm mono asl-navy-text" data-testid={totalTid}>{fmtUSD0(total)}</div>
            </div>
            {loading ? (
                <div className="asl-shimmer w-full rounded-lg" style={{ height: 140 }} />
            ) : data.every((r) => r.cost === 0) ? (
                <EmptyState icon={Banknote} title="No cost data yet" description="Runs once payroll lines are prepared." />
            ) : (
                <div data-testid={chartTid}>
                <ResponsiveContainer width="100%" height={140}>
                    <AreaChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={c.series[0]} stopOpacity={0.45} />
                                <stop offset="100%" stopColor={c.series[0]} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke={c.grid} strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={fmtDateShort} interval={5} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: c.axis }} />
                        <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: c.axis }} width={44}
                            tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v} />
                        <Tooltip
                            formatter={(v) => fmtUSD2(v)}
                            labelFormatter={fmtDateShort}
                            contentStyle={{ background: c.tooltipBg, color: c.tooltipText, border: "1px solid var(--border-default)" }}
                            itemStyle={{ color: c.tooltipText }}
                            labelStyle={{ color: c.tooltipText }}
                        />
                        <Area type="monotone" dataKey="cost" stroke={c.series[0]} strokeWidth={2} fill={`url(#${gradientId})`} />
                    </AreaChart>
                </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Alerts mini tile                                                   */
/* ------------------------------------------------------------------ */
export function AlertsMiniTile({ count, breakdown, loading, onNav, style }) {
    const tone = count > 0 ? "amber" : "quiet";
    return (
        <div className="asl-bento-tile asl-bento-alerts-mini" data-testid="md-tile-alerts-mini"
            style={{ gridColumn: "1 / span 3", gridRow: "3", ...style }}>
            <div className="flex items-center gap-3 mb-3">
                <div className={`asl-alerts-mini__num asl-alerts-mini__num--${tone}`} data-testid="md-alerts-count">
                    {loading ? "…" : count}
                </div>
                <div>
                    <div className="asl-section-title" style={{ marginBottom: 2 }}>Attention needed</div>
                    <div className="text-xs" style={{ color: "var(--asl-text-3)" }}>{count === 0 ? "All clear" : "Requires review"}</div>
                </div>
            </div>
            <ul className="space-y-2">
                {breakdown.map((r) => (
                    <li key={r.label}>
                        <button
                            type="button"
                            onClick={() => onNav?.(r.link)}
                            className="w-full flex items-center justify-between text-left text-sm py-1.5 px-2 rounded transition"
                            data-testid={`md-alert-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                            style={{ color: r.value > 0 ? "var(--asl-text-1)" : "var(--asl-text-3)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--asl-bg-alt)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                            <span>{r.label}</span>
                            <span className={`mono font-semibold ${r.value > 0 ? "asl-crimson-text" : ""}`}>{r.value}</span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Recent activity tile                                               */
/* ------------------------------------------------------------------ */
export function RecentActivityTile({ items, loading, emptyMessage = "Actions on this module will surface here.", style }) {
    return (
        <div className="asl-bento-tile" data-testid="md-tile-recent-activity"
            style={{ gridColumn: "4 / span 4", gridRow: "3", ...style }}>
            <div className="asl-section-title mb-3">Recent activity</div>
            {loading ? (
                <ul className="space-y-3">
                    {[0, 1, 2, 3].map((i) => (
                        <li key={i} className="space-y-1.5">
                            <div className="asl-shimmer h-3 w-2/3" />
                            <div className="asl-shimmer h-3 w-1/3" />
                        </li>
                    ))}
                </ul>
            ) : items.length === 0 ? (
                <EmptyState icon={Clock} title="No activity yet" description={emptyMessage} />
            ) : (
                <ul>
                    {items.slice(0, 5).map((a) => (
                        <li key={a.id} className="py-2 flex items-start justify-between gap-3" style={{ borderTop: "1px solid var(--asl-divider)" }}
                            data-testid={`md-activity-${a.id}`}>
                            <div className="min-w-0">
                                <div className="text-sm">
                                    <span className="mono text-xs asl-crimson-text">{a.action}</span>
                                    <span className="mx-1.5 text-[color:var(--asl-text-3)]">·</span>
                                    <span>{a.entity_type}</span>
                                </div>
                                <div className="text-xs truncate mt-0.5" style={{ color: "var(--asl-text-2)" }}>{a.actor_email}</div>
                            </div>
                            <div className="text-xs whitespace-nowrap" style={{ color: "var(--asl-text-3)" }}>{fmtDateTime(a.timestamp)}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Top rows tile — same visual for "Top projects" (module scope) and  */
/* "Top modules" (landing scope). Callers pass items with `label` +   */
/* `cost` fields, and an optional per-row onClick.                    */
/* ------------------------------------------------------------------ */
export function TopRowsTile({
    title,
    rows,
    loading,
    emptyIcon = Banknote,
    emptyTitle = "Nothing to show yet",
    emptyDescription = "Populates as data flows in.",
    onRowClick,
    style,
    testid = "md-tile-top-projects",
}) {
    const total = rows.reduce((s, r) => s + (r.cost || 0), 0);
    return (
        <div className="asl-bento-tile" data-testid={testid}
            style={{ gridColumn: "8 / span 3", gridRow: "3", ...style }}>
            <div className="asl-section-title mb-3">{title}</div>
            {loading ? (
                <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="space-y-1.5">
                            <div className="asl-shimmer h-3 w-full" />
                            <div className="asl-shimmer h-2 w-2/3" />
                        </div>
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />
            ) : (
                <ul className="space-y-3">
                    {rows.map((r) => {
                        const pct = total ? Math.round(((r.cost || 0) / total) * 100) : 0;
                        const clickable = !!onRowClick && !!r.id;
                        return (
                            <li key={r.id || r.label} data-testid={`md-project-${r.label}`}>
                                <button
                                    type="button"
                                    disabled={!clickable}
                                    onClick={clickable ? () => onRowClick(r) : undefined}
                                    className="w-full text-left"
                                    style={{ cursor: clickable ? "pointer" : "default" }}
                                >
                                    <div className="flex items-center justify-between text-xs mb-1">
                                        <span className="truncate" style={{ color: "var(--asl-text-1)" }}>{r.label}</span>
                                        <span className="mono asl-navy-text font-semibold">{fmtUSD0(r.cost)}</span>
                                    </div>
                                    <div className="asl-bar-track">
                                        <div className="asl-bar-fill" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="text-[10px] mt-1" style={{ color: "var(--asl-text-3)" }}>{pct}% of total</div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Quick-add tile                                                     */
/* ------------------------------------------------------------------ */
export function QuickAddTile({ actions, onNav, style }) {
    return (
        <div className="asl-bento-tile" data-testid="md-tile-quick-add"
            style={{ gridColumn: "11 / span 2", gridRow: "3", ...style }}>
            <div className="asl-section-title mb-3">Quick add</div>
            <div className="asl-quick-add">
                {actions.map(({ icon: Icon, label, to }) => (
                    <button
                        key={label}
                        type="button"
                        onClick={() => onNav?.(to)}
                        className="asl-quick-add__btn"
                        data-testid={`md-quick-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <Icon className="h-5 w-5" style={{ color: "var(--asl-crimson-2)" }} />
                        <span>{label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Alert row (below the bento grid)                                   */
/* ------------------------------------------------------------------ */
export function AlertRow({ alert, onClick }) {
    const Icon = alert.severity === "critical" ? AlertOctagon : alert.severity === "warn" ? AlertTriangle : Info;
    const tone = alert.severity === "critical" ? "border-rose-400 bg-rose-50 text-rose-900"
        : alert.severity === "warn" ? "border-amber-400 bg-amber-50 text-amber-900"
            : "border-blue-400 bg-blue-50 text-blue-900";
    return (
        <div className={`flex items-center gap-3 p-3 border-l-4 rounded-r ${tone} cursor-pointer hover:opacity-90`}
            onClick={onClick} role="link" data-testid="md-alert">
            <Icon className="h-4 w-4 shrink-0" />
            <div className="flex-1 text-sm">{alert.message}</div>
            <ArrowRight className="h-4 w-4" />
        </div>
    );
}
